
import AppFileDropzone from '@/components/ui/AppFileDropzone.vue';
import VideoTrimModal from '@/components/post/VideoTrimModal.vue';
import { Component, Prop, Vue } from 'vue-property-decorator';

type MediaKind = 'image' | 'video';

export type PostMediaInputPayload = {
  kind: MediaKind;
  file: File | null;
  durationSeconds?: number | null;
};

@Component({
  name: 'post-media-dropzone',
  components: { AppFileDropzone, VideoTrimModal },
})
export default class PostMediaDropzone extends Vue {
  @Prop({ required: false, type: String })
  readonly id?: string;

  /** Optional max trim length hint (e.g. 30 for Google My Business). */
  @Prop({ required: false, type: Number, default: null })
  readonly maxSegmentDurationSeconds!: number | null;

  loadedFile: File | null = null;
  loadedKind: MediaKind | null = null;
  loadedVideoDuration: number | null = null;
  objectUrl = '';
  showTrimModal = false;

  get fileSource(): string {
    return this.objectUrl;
  }

  get isVideo(): boolean {
    return this.loadedKind === 'video';
  }

  detectKind(file: File): MediaKind {
    return file.type.startsWith('video/') ? 'video' : 'image';
  }

  updateObjectUrl() {
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = '';
    }
    if (this.loadedFile) {
      this.objectUrl = URL.createObjectURL(this.loadedFile);
    }
  }

  beforeDestroy() {
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
    }
  }

  /** Emits current media state to the parent, including video duration when known. */
  emitInput(durationSeconds?: number | null) {
    const payload: PostMediaInputPayload = {
      kind: this.loadedKind || 'image',
      file: this.loadedFile,
    };
    if (durationSeconds !== undefined) {
      payload.durationSeconds = durationSeconds;
    }
    this.$emit('input', payload);
  }

  onRemoveLoadedFile() {
    this.loadedFile = null;
    this.loadedKind = null;
    this.loadedVideoDuration = null;
    this.updateObjectUrl();
    this.emitInput(null);
  }

  onFileInput(file: File) {
    this.loadedFile = file;
    this.loadedKind = this.detectKind(file);
    this.loadedVideoDuration = null;
    this.updateObjectUrl();
    this.emitInput(this.loadedKind === 'video' ? null : undefined);
  }

  /** Reads video length from the preview element once metadata is available. */
  onVideoMetadataLoaded(event: Event) {
    const video = event.target as HTMLVideoElement;
    if (!Number.isFinite(video.duration)) {
      return;
    }
    this.loadedVideoDuration = video.duration;
    this.emitInput(video.duration);
  }

  /** Applies media from an external source (e.g. Instagram import). */
  setImportedFile(file: File, kind?: MediaKind) {
    this.loadedFile = file;
    this.loadedKind = kind || this.detectKind(file);
    this.updateObjectUrl();
    this.emitInput(this.loadedKind === 'video' ? null : undefined);
  }

  /** Replaces the current video with a trimmed export from the modal. */
  onVideoTrimmed(file: File) {
    this.loadedFile = file;
    this.loadedKind = 'video';
    this.loadedVideoDuration = null;
    this.updateObjectUrl();
    this.emitInput(null);
  }
}
